import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Icon} from "@blueprintjs/core";
import {Link} from 'react-router';

import {SearchableDataGrid} from "../../../components/tables/data-grid";
import {recordings} from "../../../services/api";
import {DataPageContext} from "../context";
import {timeAgo} from "../../../utils/time";

import {ElemsPreviewProvider, ElemsPreview, ContextTags} from '@auravisionlabs/aura-react';


// --------------------------------------------------------- Columns
const RecordingModelColumns = [
    {
        key: 'thumbnails',
        name: 'Preview',
        locked: true,
        width: 250,
        formatter: ({row}) =>
            <Link to={'/recs/' + row.id} style={{display: 'block', width: '100%', height: '100%'}}>
                {row?.thumbnails?.[0]?.url ? (
                    <ElemsPreview recording={row} maxHeight={200} />
                ) : (
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%'}}>
                        <Icon icon="media" size={40} color="#8a9ba8" />
                    </div>
                )}
            </Link>,
    },
    {
        key: 'contexts',
        name: 'Contexts',
        width: 200,
        resizable: true,
        filterable: true,
        formatter: ({row}) => <ContextTags recording={row} />,
    },
    {
        key: 'id',
        name: 'Recording ID',
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
        formatter: (params) =>
            <Link to={'/recs/' + params.row.id}>
                {params.row.id}
            </Link>
    },
    {
        key: 'organisation.name',
        name: 'Organisation Name',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.organisation ?
            <Link to={'/orgs/' + params.row.organisation._id}>
                {params.row.organisation.name}
            </Link> : ''
    },
    {
        key: 'location.name',
        name: 'Location Name',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.location ?
            <Link to={'/locs/' + params.row.location._id}>
                {params.row.location.name}
            </Link> : ''
    },
    {
        key: 'location.status',
        name: 'Location Status',
        width: 100,
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.location?.status || ''
    },
    {
        key: 'cameraServer.name',
        name: 'Device',
        width: 100,
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.cameraServer ?
            <Link to={'/devs/' + params.row.cameraServer._id}>
                {params.row.cameraServer.name}
            </Link> : ''
    },
    {
        key: 'cameraServer.hardwareClass',
        name: 'Device Type',
        width: 100,
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.cameraServer?.hardwareClass || ''
    },
    {
        key: 'name',
        name: 'Camera Name',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (j) => j.row?.name || 'Not present',
    },
    {
        key: 'createdAt',
        name: 'Created',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt',
        name: 'Updated',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
];


const ManyRecs = ({search}) => {
    const {setLoading, setNav, setCount} = useContext(DataPageContext);
    const [data, setData] = useState(null);

    useEffect(() => {
        let searchKeys = ['org', 'loc', 'dev'].filter((s) => search.has(s));
        if (searchKeys.length > 1) {
            setLoading({status: false, location: "Can only filter by one of organisation, location or device"});
            setData(Error("Can only filter by one of organisation, location or device"));
        } else {
            console.log("Loading recordings");
            setLoading({status: true, location: "Loading cameras..."});
            let fetch = {
                org: {withinOrganisations: search.get('org')},
                loc: {withinLocations: search.get('loc')},
                dev: {withinCameraServers: search.get('dev')},
                null: {}
            }[searchKeys.length ? searchKeys[0] : null];
            recordings.list({
                ...fetch, include: 'locationDetails,organisationDetails,cameraServerDetails,singleThumbnail,statElems,lineContexts,areaContexts',
            }).then(response => {
                console.log("Loaded " + response.length + " recs");

                const rec = response[0];
                const org = rec?.organisation;
                const loc = rec?.location;
                const dev = rec?.cameraServer;

                let description = {
                    org: org ? org.name + " Cameras" : "No cameras for organisation " + search.get('org'),
                    loc: loc ? "Cameras in " + loc.name : "No cameras for location " + search.get('loc'),
                    dev: dev ? "Cameras in " + dev.name : "No cameras for device " + search.get('dev'),
                    null: "All Cameras",
                }[searchKeys.length ? searchKeys[0] : null];

                const navLeft = [];
                if (rec && searchKeys.length) {
                    if (org && (search.has('org') || search.has('loc') || search.has('dev'))) {
                        navLeft.push({
                            name: org.name,
                            path: "/orgs/" + org._id,
                            icon: "office",
                            subpaths: [
                                {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + org._id},
                                {name: "Devices", icon: "desktop", path: '/devs?org=' + org._id},
                                {name: "Cameras", icon: "camera", path: '/recs?org=' + org._id},
                                {name: "Users", icon: "people", path: '/orgs/' + org._id + '/users'},
                            ]
                        });
                    }
                    if (loc && (search.has('loc') || search.has('dev'))) {
                        navLeft.push({
                            name: loc.name,
                            path: "/locs/" + loc._id,
                            icon: "area-of-interest",
                            subpaths: [
                                {name: "Devices", icon: "desktop", path: '/devs?loc=' + loc._id},
                                {name: "Cameras", icon: "camera", path: '/recs?loc=' + loc._id},
                            ]
                        });
                    }
                    if (dev && search.has('dev')) {
                        navLeft.push({
                            name: dev.name,
                            path: "/devs/" + dev._id,
                            icon: "desktop",
                            subpaths: [
                                {name: "Cameras", icon: "camera", path: '/recs?dev=' + dev._id},
                            ]
                        });
                    }
                }

                if (navLeft.length) setNav({left: navLeft});
                setLoading({status: false, location: description});
                response.forEach(row => {
                    const lc = (row.lineContexts || []).map(lc => {
                        const meta = lc.metadata?.length
                            ? lc.metadata.map(m => `${m.key}: ${m.value}`).join(', ')
                            : lc.withinLocationMovementKey || '';
                        return lc.type + (meta ? `: ${meta}` : '');
                    });
                    const ac = (row.areaContexts || []).map(ac => ac.taxonomy || ac.type);
                    row.contexts = [...lc, ...ac].join(', ');
                });
                requestAnimationFrame(() => setData(response));
            }).catch(error => {
                console.error(error);
                setLoading({status: false, location: "Failed to load cameras..."});
                setData(error);
            });
        }
    }, [search]);

    const [selectedAreaContexts, setSelectedAreaContexts] = useState([]);
    const [selectedLineContexts, setSelectedLineContexts] = useState([]);

    const onTagHover = useCallback((tag) => {
        setSelectedAreaContexts(tag?.elemType === 'area' ? [tag.key] : []);
        setSelectedLineContexts(tag?.elemType === 'curve' ? [tag.type] : []);
    }, []);

    return (
        <ElemsPreviewProvider control="both" selectedAreaContexts={selectedAreaContexts} selectedLineContexts={selectedLineContexts} onTagHover={onTagHover}>
            <SearchableDataGrid
                columns={RecordingModelColumns}
                rowHeight={200}
                data={data ?? []}
                defaultSort='createdAt'
                defaultSortDirection='DESC'
                onCount={(filtered, total) => setCount({filtered, total})}
            />
        </ElemsPreviewProvider>
    );
}

export {ManyRecs};
